import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Optional, Output, SimpleChanges } from '@angular/core';
import { FormButton } from '@app/core/components/form-field-button/form-field-button.component';
import { GridModalComponent } from '@app/core/components/grid/grid.decorators';
import { Item } from '@app/core/components/listbox/listbox.component';
import { ModalService } from '@app/core/services/modal.service';
import { RememberService } from '@app/core/services/remember.service';
import { ToastService } from '@app/core/services/toast.service';
import { UtilityService } from '@app/core/services/utility.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { CustomerEditGet, CustomerService } from '@app/services/customer.service';
import { EconomyService, PaymentTypeItem, PriceListItem, ShiftTypeItem, TaxItem } from '@app/services/economy.service';
import { BusinesscoItem, LogisticService } from '@app/services/logistic.service';
import { MinutePrecisionItem, ProjectGet, ProjectManagerItem, ProjectService } from '@app/services/project.service';
import { TypeaheadService } from '@app/services/typeahead.service';

@GridModalComponent('project')
@Component({
    selector: 'tt-project',
    templateUrl: './project.component.html',
    styleUrls: ['./project.component.css'],
    standalone: false
})
export class ProjectComponent implements OnChanges, OnInit {
    @Input()
    public params?: { project_keyno: string | number };

    @Output()
    public submit = new EventEmitter<{ event: 'delete' | 'save' }>();

    public get?: ProjectGet;

    @Output()
    public getLoaded = new EventEmitter<ProjectGet>();

    public companies: BusinesscoItem[] = [];

    public projectManagers: ProjectManagerItem[] = [];

    public shiftTypes: ShiftTypeItem[] = [];

    public minutePrecisions: MinutePrecisionItem[] = [];

    public pricelists: PriceListItem[] = [];

    public paymentTypes: PaymentTypeItem[] = [];

    public taxes: TaxItem[] = [];

    public customerButtons: FormButton[] = [
        { id: 'new', icon: 'fas fa-plus', type: 'primary', goto: 'customeredit', gotoParms: '0' },
        {
            id: 'goto',
            icon: 'fas fa-chevron-right',
            type: 'primary',
            onClick: () =>
                this.navigator?.sequence([
                    { to: 'customersmenu', parms: '' + this.get!.cust_no, options: { location: 'replace' } },
                    { to: 'customeredit', parms: '' + this.get?.cust_no },
                ]),
        },
        // { id: 'goto', icon: 'fas fa-chevron-right', type: 'primary', goto: 'customersmenu', gotoParms: () => '' + this.get!.cust_no },
    ];

    public deliveryCustomerButtons: FormButton[] = [
        { id: 'new', icon: 'fas fa-plus', type: 'primary', goto: 'customeredit', gotoParms: '0', beforeGoto: () => this.rememberDeliveryCustomer() },
        { id: 'goto', icon: 'fas fa-chevron-right', type: 'primary', goto: 'customersmenu', gotoParms: () => '' + this.get!.delivery_cust_no },
    ];

    public saving: boolean = false;

    public deleting: boolean = false;

    constructor(
        private project: ProjectService,
        private logistic: LogisticService,
        private economy: EconomyService,
        private remember: RememberService,
        private typeahead: TypeaheadService,
        private customer: CustomerService,
        private utility: UtilityService,
        private modal: ModalService,
        private toast: ToastService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        this.navigator = useNavigatorFor(navigator, this);
    }

    public searchCustomers = () => {
        return this.typeahead.searchCustomer({ cust_name: this.get?.cust_name ?? '', custtype: 'C', lastselect: 'SEARCH' });
    };

    public async onCustomerSelected(item?: Item) {
        if (!!this.get) {
            this.get.cust_no = item?.item_id ?? '0';
            this.get.cust_name = item?.item_name ?? '';

            this.setValuesFromCustomer();

            console.log(this.get);
        }
    }

    public searchDeliveryCustomers = () => {
        return this.typeahead.searchCustomer({ cust_name: this.get?.show_delivery_cust_name ?? '', custtype: 'C', lastselect: 'SEARCH' });
    };

    public async onDeliveryCustomerSelected(item?: Item) {
        if (!!this.get) {
            this.get.delivery_cust_no = item?.item_id ?? '0';
            this.get.show_delivery_cust_name = item?.item_name ?? '';

            if (this.get.delivery_cust_no > '0') {
                const customer = await this.customer.getCustomerEdit({ cust_no: this.get.delivery_cust_no });
                this.get.show_delivery_cust_name = customer.cust_name;
            }
        }
    }

    public async onTaxKeynoChanged() {
        if (!!this.get) {
            const tax = await this.economy.getTax({ tax_keyno: this.get.tax_keyno });
            this.get.tax_pst = tax.tax_pst;
            this.get.contract_amount_incl_tax = this.calculateAmountInclTax(this.get.contract_amount_ex_tax, this.get.tax_pst);
            this.get.max_outstanding_amount_incl_tax = this.calculateMaxOutstandingAmount(this.get.contract_amount_incl_tax, this.get.max_outstanding_pst);
        }
    }

    public onContractAmountExTaxChanged() {
        if (!!this.get) {
            this.get.contract_amount_incl_tax = this.calculateAmountInclTax(this.get.contract_amount_ex_tax, this.get.tax_pst);
            this.get.max_outstanding_amount_ex_tax = this.calculateMaxOutstandingAmount(this.get.contract_amount_ex_tax, this.get.max_outstanding_pst);
            this.get.max_outstanding_amount_incl_tax = this.calculateMaxOutstandingAmount(this.get.contract_amount_incl_tax, this.get.max_outstanding_pst);
        }
    }

    public onMaxOutstandingPercentageChanged() {
        if (!!this.get) {
            this.get.max_outstanding_amount_ex_tax = this.calculateMaxOutstandingAmount(this.get.contract_amount_ex_tax, this.get.max_outstanding_pst);
            this.get.max_outstanding_amount_incl_tax = this.calculateMaxOutstandingAmount(this.get.contract_amount_incl_tax, this.get.max_outstanding_pst);
        }
    }

    public async onSave() {
        if (!this.get) return;

        try {
            this.saving = true;

            const { errorcode, errormessage } = await this.project.saveProject(this.get);

            if (errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            } else {
                this.toast.addToast({
                    id: `project.save.${Date.now()}`,
                    type: 'success',
                    title: 'saved!',
                    // translateTitle: true,
                    message: 'changes_have_been_saved',
                    // translateMessage: true,
                    hideDelay: 4000,
                });

                this.submit.emit({ event: 'save' });
                this.navigator?.back();
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.saving = false;
        }
    }

    public async deleteProject() {
        if (!this.get) return;

        try {
            this.deleting = true;
            const confirmed = await this.modal.openConfirmDialog({ type: 'danger', title: 'confirm_delete_title', translateTitle: true, message: 'confirm_delete_message', translateMessage: true });

            if (!confirmed) return;

            const { errorcode, errormessage } = await this.project.deleteProject(this.get);

            if (errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            } else {
                this.submit.emit({ event: 'delete' });
                this.navigator?.back();
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.deleting = false;
        }
    }

    private calculateAmountInclTax(amount: string | number, taxPercentage: string | number): number {
        return this.utility.parseNumber(amount) + (this.utility.parseNumber(amount) * this.utility.parseNumber(taxPercentage)) / 100;
    }

    private calculateMaxOutstandingAmount(amount: string | number, maxOutstandingPercentage: string | number): number {
        return (this.utility.parseNumber(amount) * this.utility.parseNumber(maxOutstandingPercentage)) / 100;
    }

    private async rememberDeliveryCustomer() {
        return await Promise.all([this.remember.remember('customer.new_customer_in_progress', '1'), this.remember.remember('w_project.new_cust_to_field', 'delivery_cust_no')]);
    }

    private async setValuesFromCustomer() {
        if (!!this.get && this.get.cust_no > '0') {
            const customer = await this.customer.getCustomerEdit({ cust_no: this.get.cust_no });
            const keys: (keyof (CustomerEditGet | ProjectGet))[] = Object.keys(customer).filter((key) => key in this.get!) as (keyof (CustomerEditGet | ProjectGet))[];

            for (let key of keys) {
                if (key !== 'businessco_no' && !!customer[key]) {
                    this.get[key] = customer[key];
                }
            }

            if (!!customer.cust_paym_id) this.get.payment_id = customer.cust_paym_id;
            if (!!customer.delivery_cust_name) this.get.show_delivery_cust_name = customer.delivery_cust_name;
        }
    }

    private async loadGet() {
        if (!!this.params) {
            this.get = await this.project.getProject(this.params);
            this.getLoaded.emit(this.get);
        }
    }

    private async loadCompanies() {
        this.companies = await this.logistic.getCompanies();
    }

    private async loadProjectManagers() {
        this.projectManagers = await this.project.getProjectManagers();
    }

    private async loadShiftTypes() {
        this.shiftTypes = await this.economy.getShiftTypes({ only_active: '1' });
    }

    private async loadMinutePrecisions() {
        this.minutePrecisions = await this.project.getMinutePrecisions();
    }

    private async loadPricelist() {
        this.pricelists = await this.economy.getPriceLists();
    }

    private async loadPaymentTypes() {
        this.paymentTypes = await this.economy.getPaymentTypes();
    }

    private async loadTaxes() {
        this.taxes = await this.economy.getTaxes();
    }

    private async init() {
        this.loadCompanies();
        this.loadProjectManagers();
        this.loadShiftTypes();
        this.loadMinutePrecisions();
        this.loadPricelist();
        this.loadPaymentTypes();
        this.loadTaxes();
        await this.loadGet();
        this.navigator?.setHeading?.(this.get?.project_name ?? '');
    }

    ngOnInit(): void {
        if (!!this.navigator) {
            this.navigator.setSize?.({ height: '80vh' });
            this.params = this.navigator.params as { project_keyno: string | number };
            this.init();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']) {
            this.init();
        }
    }
}
